import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Waves, Bath, Sailboat, Baby, Dog, Gem, Gamepad2, Flame, TrendingUp, Star, ArrowUpRight } from "lucide-react";
import { api, money } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { SearchBar } from "@/components/site/SearchBar";
import { PropertyCard } from "@/components/site/PropertyCard";
import { LocalSearch } from "@/components/site/LocalSearch";
import { Stars } from "@/components/site/Stars";
import { CITY_GUIDES } from "@/lib/guides";

const HEROES = [
  "https://customer-assets-v7afamib.emergentagent.net/job_elite-booking-10/artifacts/ccx29jan_f68d0e5a-7cde-4064-9ab5-522a1b4c430c.png",
  "https://customer-assets-v7afamib.emergentagent.net/job_elite-booking-10/artifacts/a0ja5s4p_DJI_0236.webp",
  "https://customer-assets-v7afamib.emergentagent.net/job_elite-booking-10/artifacts/5krwy2t0_DSC05819.webp",
  "https://customer-assets-v7afamib.emergentagent.net/job_elite-booking-10/artifacts/74t8v19m_Screenshot_28-7-2026_134547_www.airbnb.com.jpeg",
];
const AICONS = { waves: Waves, bath: Bath, sailboat: Sailboat, baby: Baby, dog: Dog, gem: Gem, "gamepad-2": Gamepad2, flame: Flame };

const Section = ({ children, className = "" }) => (
  <section className={`mx-auto max-w-7xl px-5 lg:px-8 ${className}`}>{children}</section>
);

export default function Home() {
  const [slide, setSlide] = useState(0);
  const [props, setProps] = useState([]);
  const [amenities, setAmenities] = useState([]);
  const [dests, setDests] = useState([]);
  const [testi, setTesti] = useState([]);
  const [stats, setStats] = useState(null);
  const [eventProps, setEventProps] = useState([]);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    api.get("/properties?sort=rating").then((r) => {
      setTotal(r.data.count ?? r.data.properties.length);
      setProps(r.data.properties.slice(0, 6));
    });
    api.get("/properties?amenity=events").then((r) => {
      const order = { wedding: 0, gathering: 1 };
      setEventProps([...r.data.properties].sort((a, b) => (order[a.event_type] ?? 9) - (order[b.event_type] ?? 9)));
    });
    api.get("/amenities").then((r) => setAmenities(r.data.amenities));
    api.get("/destinations").then((r) => setDests(r.data.destinations));
    api.get("/testimonials").then((r) => setTesti(r.data.testimonials));
    api.get("/stats").then((r) => setStats(r.data));
    const t = setInterval(() => setSlide((s) => (s + 1) % HEROES.length), 5500);
    return () => clearInterval(t);
  }, []);

  return (
    <div>
      {/* HERO */}
      <div className="relative min-h-[92vh] w-full overflow-hidden">
        {HEROES.map((src, i) => (
          <motion.div key={i} className="absolute inset-0" initial={false}
            animate={{ opacity: i === slide ? 1 : 0, scale: i === slide ? 1.06 : 1 }}
            transition={{ opacity: { duration: 1.2 }, scale: { duration: 6, ease: "linear" } }}>
            <img src={src} alt="Luxury vacation rental" className="h-full w-full object-cover" />
          </motion.div>
        ))}
        <div className="absolute inset-0 hero-fade" />
        <div className="relative z-10 mx-auto flex min-h-[92vh] max-w-7xl flex-col justify-center px-5 pb-24 pt-20 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="max-w-2xl">
            <span className="overline text-gold">Elite STR Hosting Co.</span>
            <h1 className="mt-4 text-5xl font-medium leading-[0.95] text-white sm:text-6xl lg:text-7xl">
              The finest homes in Texas, hosted flawlessly.
            </h1>
            <p className="mt-5 max-w-lg text-lg text-white/80">
              Book curated luxury vacation rentals direct — or let us turn your property into a thriving business.
            </p>
            <div data-testid="hero-trust-bar" className="mt-6 inline-flex flex-wrap items-center gap-2 rounded-full glass px-4 py-2 text-sm">
              <span className="flex items-center gap-1 font-bold text-gold">
                <Star size={15} className="fill-gold text-gold" /> {stats?.rating ?? 4.9}
              </span>
              <span className="text-ink/30">·</span>
              <span className="font-medium text-ink">
                {(stats?.review_count ?? 1200).toLocaleString()}+ reviews from Airbnb, VRBO &amp; Google
              </span>
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.25 }} className="mt-10">
            <SearchBar />
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.4 }}
            className="mt-6 flex flex-wrap items-center gap-3">
            <Link to="/rentals">
              <Button data-testid="hero-view-all-btn"
                className="rounded-full bg-white px-7 py-6 text-base font-semibold text-ink shadow-lg transition-colors hover:bg-camel hover:text-white">
                View all properties <ArrowRight size={18} className="ml-1.5" />
              </Button>
            </Link>
            <span className="text-sm text-white/70">Browse every home we host — no fees, book direct.</span>
          </motion.div>
        </div>
        <div className="absolute bottom-6 left-1/2 z-10 flex -translate-x-1/2 gap-2">
          {HEROES.map((_, i) => (
            <button key={i} onClick={() => setSlide(i)} data-testid={`hero-dot-${i}`}
              className={`h-1.5 rounded-full transition-all ${i === slide ? "w-8 bg-white" : "w-4 bg-white/40"}`} />
          ))}
        </div>
      </div>

      {/* AMENITIES */}
      <Section className="pt-16">
        <div className="mb-6 flex items-end justify-between">
          <div>
            <span className="overline">Find your vibe</span>
            <h2 className="mt-2 text-3xl lg:text-4xl">Browse by amenity</h2>
          </div>
        </div>
        <div className="flex gap-4 overflow-x-auto pb-2 no-scrollbar">
          {amenities.map((a) => {
            const Icon = AICONS[a.icon] || Gem;
            return (
              <Link key={a.key} to={`/rentals?amenity=${a.key}`} data-testid={`amenity-${a.key}`}
                className="group flex min-w-[140px] flex-col items-center gap-3 rounded-2xl border border-black/5 bg-white px-5 py-6 text-center transition-colors hover:border-camel">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-cream transition-colors group-hover:bg-camel group-hover:text-white">
                  <Icon size={22} strokeWidth={1.5} />
                </span>
                <span className="text-sm font-medium text-ink">{a.label}</span>
              </Link>
            );
          })}
        </div>
      </Section>

      {/* FEATURED */}
      <Section className="pt-16">
        <div className="mb-8 flex items-end justify-between">
          <div>
            <span className="overline">Handpicked</span>
            <h2 className="mt-2 text-3xl lg:text-4xl">Featured luxury properties</h2>
          </div>
          <Link to="/rentals" className="hidden items-center gap-1 text-sm font-semibold text-ink hover:text-camel sm:flex">
            View all <ArrowRight size={16} />
          </Link>
        </div>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {props.map((p, i) => <PropertyCard key={p.id} p={p} index={i} />)}
        </div>
        <div className="mt-10 flex justify-center">
          <Link to="/rentals">
            <Button data-testid="view-all-properties-btn" className="rounded-full btn-ink px-8 py-6 text-base font-semibold">
              View all {total || ""} properties <ArrowRight size={18} className="ml-1.5" />
            </Button>
          </Link>
        </div>
      </Section>

      {/* WEDDINGS & GATHERINGS */}
      {eventProps.length > 0 && (
        <div className="mt-20 bg-cream py-16">
          <Section>
            <div className="mb-8 flex items-end justify-between">
              <div>
                <span className="overline text-camel">Weddings & Gatherings</span>
                <h2 className="mt-2 text-3xl lg:text-4xl">Host your celebration</h2>
                <p className="mt-3 max-w-xl text-taupe">
                  From weddings and bachelorette parties to family reunions and corporate retreats — these estates are built for bringing everyone together in style.
                </p>
              </div>
              <Link to="/weddings" className="hidden items-center gap-1 text-sm font-semibold text-ink hover:text-camel sm:flex">
                Plan your event <ArrowRight size={16} />
              </Link>
            </div>
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {eventProps.map((p, i) => <PropertyCard key={p.id} p={p} index={i} />)}
            </div>
            <div className="mt-8 sm:hidden">
              <Link to="/weddings"><Button className="w-full rounded-full btn-ink">Plan your event</Button></Link>
            </div>
          </Section>
        </div>
      )}

      {/* DESTINATIONS + MAP */}
      <Section className="pt-20">
        <span className="overline">Explore Texas</span>
        <h2 className="mt-2 text-3xl lg:text-4xl">Featured destinations</h2>
        <div className="mt-8 grid gap-6 lg:grid-cols-2">
          <div className="grid grid-cols-2 gap-4">
            {dests.map((d, i) => (
              <Link key={d.name} to={`/rentals?q=${encodeURIComponent(d.name)}`} data-testid={`dest-${i}`}
                className="group relative aspect-[4/3] overflow-hidden rounded-2xl">
                <img src={d.image} alt={d.name} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                <div className="absolute bottom-4 left-4 text-white">
                  <div className="text-xl">{d.name}</div>
                  <div className="text-xs text-white/70">{d.count} homes</div>
                </div>
              </Link>
            ))}
          </div>
          <div className="overflow-hidden rounded-2xl border border-black/5">
            <iframe title="Texas map" className="h-full min-h-[360px] w-full grayscale-[0.2]"
              src="https://www.openstreetmap.org/export/embed.html?bbox=-98.75%2C29.30%2C-98.05%2C30.00&layer=mapnik" />
          </div>
        </div>
      </Section>

      {/* EXPLORE THE AREA */}
      <div className="mt-20 bg-cream py-16">
        <Section>
          <div className="mb-8 max-w-2xl">
            <span className="overline text-camel">Explore the area</span>
            <h2 className="mt-2 text-3xl lg:text-4xl">Your guide to the Texas Hill Country</h2>
            <p className="mt-3 text-taupe">Great food, spring-fed rivers, wineries and world-famous attractions — every stay comes with a curated local guide. Start with the areas we call home.</p>
          </div>
          <div className="mb-10">
            <LocalSearch />
          </div>
          <div className="grid gap-6 md:grid-cols-3">
            {[
              { city: "Canyon Lake", to: "/rentals?q=Canyon%20Lake" },
              { city: "New Braunfels", to: "/rentals?amenity=new_braunfels" },
              { city: "San Antonio", to: "/rentals?q=San%20Antonio" },
            ].map(({ city, to }) => {
              const g = CITY_GUIDES[city];
              return (
                <Link key={city} to={to} data-testid={`area-guide-${city.toLowerCase().replace(/ /g, "-")}`}
                  className="group block rounded-3xl card-lux p-7 lift">
                  <h3 className="text-2xl text-ink">{city}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-taupe">{g.intro}</p>
                  <div className="mt-4 space-y-2">
                    {g.attractions.slice(0, 2).map((a) => (
                      <div key={a.name} className="flex items-center gap-2 text-sm text-ink">
                        <span className="h-1.5 w-1.5 rounded-full bg-camel" /> {a.name}
                      </div>
                    ))}
                  </div>
                  <span className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-camel">
                    Explore homes <ArrowUpRight size={15} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </span>
                </Link>
              );
            })}
          </div>
        </Section>
      </div>

      {/* OWNER CTA */}
      <div className="relative mt-24 overflow-hidden bg-ink py-24 text-white">
        <img src="https://customer-assets-v7afamib.emergentagent.net/job_elite-booking-10/artifacts/5krwy2t0_DSC05819.webp"
          alt="" className="absolute inset-0 h-full w-full object-cover opacity-20" />
        <Section className="relative">
          <div className="max-w-2xl">
            <span className="overline text-gold">For homeowners</span>
            <h2 className="mt-3 text-4xl lg:text-5xl">Earn more with Elite STR Hosting Co.</h2>
            <p className="mt-4 text-lg text-white/70">
              Professional photography, dynamic pricing, and multi-channel marketing — our owners see an average 32% revenue increase in their first season.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/owner-services">
                <Button data-testid="owner-cta-calc" className="rounded-full bg-white px-6 text-ink hover:bg-camel hover:text-white">
                  Free Revenue Projection <ArrowUpRight size={18} className="ml-1" />
                </Button>
              </Link>
              <Link to="/property-management">
                <Button variant="outline" className="rounded-full border-white/30 bg-transparent px-6 text-white hover:bg-white hover:text-ink">
                  How it works
                </Button>
              </Link>
            </div>
          </div>
        </Section>
      </div>

      {/* STATS */}
      {stats && (
        <Section className="py-20">
          <div className="grid grid-cols-2 gap-8 lg:grid-cols-4">
            {[
              { v: "10,000+", l: "Guests served" },
              { v: "5,000+", l: "Nights booked" },
              { v: `${stats.rating ?? 4.9}★`, l: "Average guest rating" },
              { v: `${stats.avg_occupancy}%`, l: "Average occupancy" },
            ].map((s, i) => (
              <div key={i} className="text-center">
                <div className="text-4xl font-medium text-ink lg:text-5xl">{s.v}</div>
                <div className="overline mt-2">{s.l}</div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* TESTIMONIALS */}
      <div className="bg-cream py-20">
        <Section>
          <div className="mb-10 text-center">
            <span className="overline">Loved by guests & owners</span>
            <h2 className="mt-2 text-3xl lg:text-4xl">What our community says</h2>
          </div>
          <div className="grid gap-6 md:grid-cols-3">
            {testi.map((t) => (
              <div key={t.id} className="card-lux rounded-3xl p-7">
                <Stars value={t.rating} />
                <p className="mt-4 text-lg leading-relaxed text-ink">"{t.quote}"</p>
                <div className="mt-6 flex items-center gap-3">
                  <img src={t.avatar} alt={t.name} className="h-11 w-11 rounded-full object-cover" />
                  <div>
                    <div className="text-sm font-semibold text-ink">{t.name}</div>
                    <div className="text-xs text-taupe">{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Section>
      </div>

      {/* FINAL CTA */}
      <Section className="pb-8">
        <div className="grid gap-4 rounded-3xl bg-ink p-10 text-white md:grid-cols-2 md:items-center md:p-14">
          <div>
            <TrendingUp className="mb-3 text-gold" strokeWidth={1.5} />
            <h2 className="text-3xl lg:text-4xl">Ready when you are.</h2>
            <p className="mt-3 text-white/70">Book your next escape or start earning as an owner — it takes minutes.</p>
          </div>
          <div className="flex flex-wrap gap-3 md:justify-end">
            <Link to="/rentals"><Button className="rounded-full bg-white px-6 text-ink hover:bg-camel hover:text-white">Book Your Stay</Button></Link>
            <Link to="/owner-services"><Button variant="outline" className="rounded-full border-white/30 bg-transparent px-6 text-white hover:bg-white hover:text-ink">Request Consultation</Button></Link>
          </div>
        </div>
      </Section>
    </div>
  );
}
